import { ReactNode, useContext, useState } from 'react';
import { QueryClientContext } from '@tanstack/react-query';

import { ErrorBoundary } from '@/components/core/common/boundaries/ErrorBoundary';
import { PanelFallback } from '@/components/core/common/feedback/PanelFallback';

/**
 * Per-panel boundary used around individual lesson sub-panels (Sources,
 * Chat, Doubts, Extras, Highlights carousel, Quiz, Flashcards…). One
 * panel crashing should never take down its siblings - the user can
 * keep reading text while chat is broken, or vice versa.
 *
 * Recovery: the inner `<ErrorBoundary>` is `key`ed on a local
 * `resetKey` counter. Clicking "Retry" bumps the counter, which
 * remounts the boundary fresh and re-renders the children. If the
 * crash was transient (a one-off render race, a stale prop) the panel
 * comes back. If the crash is persistent the boundary just catches
 * again and the user sees the fallback once more - at which point
 * "Reload page" is the nuclear option.
 *
 * Retry also drops the INACTIVE query cache first. Most panel crashes are a
 * bad RESPONSE, not a bad render, and a remount alone re-reads the cached
 * payload that crashed it - so until its `staleTime` ran out, Retry could not
 * recover even after the server was fixed (caught by
 * `Lesson/Viewer/SurvivesACrashingTab`). Invalidating is not enough: a stale
 * query still renders its cached data while it refetches, and that render is
 * the crash. The crashed panel is unmounted behind the fallback, so its
 * queries are exactly the inactive ones; panels still mounted keep theirs, and
 * nothing is fetched until the remount asks. The client is read optionally -
 * this boundary also renders outside a `QueryClientProvider`.
 *
 * Sits inside `PageBoundary`: this catches a render error first, the
 * page-level boundary catches it only if the panel-level one is
 * somehow itself broken (e.g. the fallback throws).
 *
 * Uses the shared `PanelFallback` so panel crashes read as the same
 * design language as step failures and on-demand failures.
 */
export function PanelBoundary({
  name,
  children,
}: {
  /** Short panel name used in the failure copy ("Sources panel failed"). */
  name: string;
  children: ReactNode;
}) {
  const [resetKey, setResetKey] = useState(0);
  const queryClient = useContext(QueryClientContext);
  const retry = () => {
    queryClient?.removeQueries({ type: 'inactive' });
    setResetKey(k => k + 1);
  };

  return (
    <ErrorBoundary
      key={resetKey}
      fallback={
        <PanelFallback
          variant='panel'
          title={`${name} panel failed`}
          description='Only this panel was affected. The rest of the lesson still works.'
          primary={{
            label: 'Retry',
            onClick: retry,
          }}
          secondary={{
            label: 'Reload page',
            onClick: () => window.location.reload(),
          }}
        />
      }
    >
      {children}
    </ErrorBoundary>
  );
}
